// ============================================================================
// Runtime Environment Configuration
// ============================================================================
// Configuration is injected at container startup via docker-entrypoint.sh,
// which generates runtime-config.js setting window.__RUNTIME_CONFIG__.
// For local "npm start", public/runtime-config.js provides fallback defaults.
//
// This mirrors the ADOBE_ENVIRONMENT pattern from guides_knowledge_graph/app/auth.py:
//   VALIDATE_URL_MAPPING = { "PROD": "...", "STG": "...", "DEV": "...", "local": "..." }
// ============================================================================

type AdobeEnvironment = 'local' | 'dev' | 'stage' | 'prod';

// Extend Window to include runtime config injected by docker-entrypoint.sh
interface RuntimeConfig {
  ADOBE_ENVIRONMENT?: string;
  API_BASE_URL?: string;
  CLIENT_ID?: string;
  /** "true" makes German selectable; anything else keeps the UI English-only. */
  ENABLE_GERMAN?: string;
}

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

// Read runtime config injected by docker-entrypoint.sh
const RUNTIME_CONFIG: RuntimeConfig = window.__RUNTIME_CONFIG__ || {};
const ADOBE_ENV: AdobeEnvironment = (
  (RUNTIME_CONFIG.ADOBE_ENVIRONMENT || 'local').toLowerCase()
) as AdobeEnvironment;

// IMS endpoints per environment (mirrors guides_knowledge_graph/app/auth.py VALIDATE_URL_MAPPING)
const IMS_ENDPOINT_MAP: Record<AdobeEnvironment, string> = {
  prod:  'https://ims-na1.adobelogin.com',
  stage:   'https://ims-na1-stg1.adobelogin.com',
  dev:   'https://ims-na1-stg1.adobelogin.com',
  local: '',
};

// API base URLs per environment.
// Deployed environments use empty string (same-origin via nginx reverse proxy).
const API_BASE_URL_MAP: Record<AdobeEnvironment, string> = {
  prod:  '',
  stage: '',
  dev:   '',
  local: 'http://localhost:8080',
};

// Platform service base URLs per environment.
// Deployed: same-origin (nginx proxies /platform/api/* to the platform service).
// Local: direct to the platform service port.
const PLATFORM_BASE_URL_MAP: Record<AdobeEnvironment, string> = {
  prod:  '',
  stage: '',
  dev:   '',
  local: 'http://localhost:8002',
};

// Data service base URLs per environment.
// All environments use same-origin: in deployed envs nginx proxies /data/api/*
// to the data service; locally CRA's setupProxy.js does the same (port 8003).
const DATA_BASE_URL_MAP: Record<AdobeEnvironment, string> = {
  prod:  '',
  stage: '',
  dev:   '',
  local: '',
};


// OAuth client IDs per environment (fill in real values)
const CLIENT_ID_MAP: Record<AdobeEnvironment, string> = {
  prod:  'flameback-ui',
  stage:   'flameback-ui',
  dev:   'flameback-ui',
  local: '',
};

const DCTX_ID_MAP: Record<AdobeEnvironment, string> = {
  prod:  'v:2,s,h,bg-c:#2C2C2CFF,efa50fb0-d46a-11f1-a10f-459b3b445aeb',
  stage: 'v:2,s,h,bg-c:#2C2C2CFF,2f2853f0-d46a-11f1-a0fb-d792540d4ed1',
  dev:   'v:2,s,h,bg-c:#2C2C2CFF,2f2853f0-d46a-11f1-a0fb-d792540d4ed1',
  local: '',
};

// Streaming answer API support per environment.
// When true, the chat UI uses SSE streaming for real-time progress and
// token-by-token answer delivery. Set to false to fall back to the
// standard request/response /answer endpoint.
const STREAMING_ENABLED_MAP: Record<AdobeEnvironment, boolean> = {
  prod:  true,
  stage: true,
  dev:   true,
  local: true,
};

// Template Designer freeform-canvas (section-stack Phase 1) affordances:
// element resize handles, per-element z-index control, header/footer slot UX, and
// the unified element palette. OFF in prod so the in-progress UX can merge without
// being exposed to customers; ON in non-prod for development + review.
const TEMPLATE_FREEFORM_ENABLED_MAP: Record<AdobeEnvironment, boolean> = {
  prod:  true,
  stage: true,
  dev:   true,
  local: true,
};

// ============================================================================
// Exported Configuration
// ============================================================================

// API Configuration
export const API_CONFIG = {
  BASE_URL: RUNTIME_CONFIG.API_BASE_URL || API_BASE_URL_MAP[ADOBE_ENV],
  PLATFORM_BASE_URL: PLATFORM_BASE_URL_MAP[ADOBE_ENV],
  DATA_BASE_URL: DATA_BASE_URL_MAP[ADOBE_ENV],
};

// OAuth Configuration for Adobe IMS
export const OAUTH_CONFIG = {
  IMS_ENDPOINT: IMS_ENDPOINT_MAP[ADOBE_ENV],
  CLIENT_ID: RUNTIME_CONFIG.CLIENT_ID || CLIENT_ID_MAP[ADOBE_ENV],
  REDIRECT_URI: window.location.origin, // Must match exactly what's registered with IMS client
  SCOPES: ["openid","AdobeID","additional_info.projectedProductContext","profile","read_organizations"],
  // Token validity threshold (7 hours in milliseconds)
  TOKEN_EXPIRY_THRESHOLD_MS: 7 * 60 * 60 * 1000,
  DCTX_ID: DCTX_ID_MAP[ADOBE_ENV],
};

// Current environment (exported for UI indicators or debugging)
export const CURRENT_ENVIRONMENT = ADOBE_ENV;

// When running locally, the backend does not validate IMS tokens,
// so the UI should skip authentication entirely.
export const IS_LOCAL = ADOBE_ENV === 'local';

// When true, the chat uses the SSE /answer/stream endpoint for real-time
// progress steps and token-by-token answer streaming.
export const STREAMING_ENABLED = STREAMING_ENABLED_MAP[ADOBE_ENV];

// When true, the Template Designer exposes the freeform-canvas Phase-1 affordances
// (resize handles, z-index control, header/footer slot UX, unified palette). The
// Template Designer mounts OUTSIDE the Site/Permissions provider stack, so it cannot
// read the org-level publishing flag — this env-mapped constant is the flag it reads
// (same mechanism as STREAMING_ENABLED). Default for `TemplateDesignerProvider`'s
// `freeformEnabled` prop, which tests override to exercise both states.
export const TEMPLATE_FREEFORM_ENABLED = TEMPLATE_FREEFORM_ENABLED_MAP[ADOBE_ENV];

// Storage keys for OAuth tokens
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'flameback_access_token',
  TOKEN_ACQUIRED_AT: 'flameback_token_acquired_at',
  SELECTED_ORG: 'flameback_selected_org',
  THEME_MODE: 'flameback_theme_mode',
  SIDEBAR_COLLAPSED: 'flameback_sidebar_collapsed',
};

// Platform service endpoints (config CRUD, providers, analytics — served by flameback-platform)
export const PLATFORM_ENDPOINTS = {
  PROVIDERS_METADATA: '/platform/api/v1/providers',
  SITES: (orgId: string) =>
    `/platform/api/v1/orgs/${orgId}/sites`,
  CREATE_SITE: (orgId: string) =>
    `/platform/api/v1/orgs/${orgId}/site`,
  VALIDATE_SITE: (orgId: string) =>
    `/platform/api/v1/orgs/${orgId}/site/validate`,
  GET_SITE: (siteId: string) => `/platform/api/v1/sites/${siteId}`,
  UPDATE_SITE: (siteId: string) => `/platform/api/v1/sites/${siteId}`,
  SITE_TEMPLATE: (siteId: string) => `/platform/api/v1/sites/${siteId}/template`,
  DOMAIN_FILTER_SETTINGS: (siteId: string) => `/platform/api/v1/sites/${siteId}/filter-settings`,
  ACCESS_CONTROL: (siteId: string) => `/platform/api/v1/sites/${siteId}/access-control`,
  ACCESS_CONTROL_SIGNING_KEY: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/access-control/signing-key`,

  // Analytics endpoints (read-only, migrated from guides_knowledge_graph)
  FEEDBACK_SUMMARY: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/feedback/summary`,
  ALL_SITES_FEEDBACK_SUMMARY: (orgId: string) =>
    `/platform/api/v1/orgs/${orgId}/analytics/feedback/summary`,
  BOTTOM_TOPICS: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/feedback/topics/bottom`,
  TOP_TOPICS: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/feedback/topics/top`,
  USER_FEEDBACK_SUMMARY: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/user-feedback/summary`,
  USER_FEEDBACK_TRENDS: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/user-feedback/trends`,
  FEEDBACK_COMPARISON: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/feedback/comparison`,
  FEEDBACK_ROWS: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/feedback/rows`,
  USAGE_TRENDS: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/usage/trends`,
  USAGE_TOP_USERS: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/usage/top-users`,
  USAGE_LLM_LATENCY: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/usage/llm-latency`,
  USAGE_TOP_TOPICS: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/usage/topics`,
  USAGE_ANSWER_SUCCESS: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/usage/answer-success`,
  USAGE_RATING_TRENDS: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/usage/rating-trends`,
  USAGE_CACHE_HIT_RATE: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/usage/cache-hit-rate`,
  UNANSWERED_QUESTIONS: (siteId: string) =>
    `/platform/api/v1/sites/${siteId}/analytics/usage/unanswered-questions`,

  // Overview dashboard (org-level landing page)
  OVERVIEW_SUMMARY: (orgId: string) =>
    `/platform/api/v1/orgs/${orgId}/analytics/overview/summary`,
  OVERVIEW_USAGE_TRENDS: (orgId: string) =>
    `/platform/api/v1/orgs/${orgId}/analytics/overview/usage-trends`,
  OVERVIEW_DOMAINS: (orgId: string) =>
    `/platform/api/v1/orgs/${orgId}/analytics/overview/domains`,
  OVERVIEW_CONTENT_DISTRIBUTION: (orgId: string) =>
    `/platform/api/v1/orgs/${orgId}/analytics/overview/content-distribution`,
};


// API Endpoints (agent service — served by guides_knowledge_graph)
export const API_ENDPOINTS = {
  ANSWER: (siteId: string) => `/flameback/api/v1/sites/${siteId}/answer`,
  ANSWER_STREAM: (siteId: string) => `/flameback/api/v1/sites/${siteId}/answer/stream`,
  SUGGESTED_QUESTIONS: (siteId: string) => `/flameback/api/v1/sites/${siteId}/suggested-questions`,
  FEEDBACK_CHECK: (siteId: string) => `/flameback/api/v1/sites/${siteId}/feedback-check`,
  FEEDBACK: (siteId: string) => `/flameback/api/v1/sites/${siteId}/feedback`,
  CONVERSATIONS: (siteId: string) => `/flameback/api/v1/sites/${siteId}/conversations`,
  CONVERSATION_TURNS: (siteId: string, conversationId: string) =>
    `/flameback/api/v1/sites/${siteId}/conversations/${conversationId}/turns`,
  DELETE_SITE: (siteId: string) => `/flameback/api/v1/sites/${siteId}`,
  DELETE_STATUS: (siteId: string) => `/flameback/api/v1/sites/${siteId}/delete-status`,
  // Publishing endpoints
  PUBLISH: (siteId: string) => `/flameback/api/v1/sites/${siteId}/publish`,
  // Content component catalog (not site-scoped)
  COMPONENTS: '/flameback/api/v1/components',
  // Placeable layout-component catalog (Title/Dates/Prolog/Search; not site-scoped)
  LAYOUT_COMPONENTS: '/flameback/api/v1/layout-components',
  // Region templates (e.g. left-toc.j2) served as raw source (not site-scoped)
  REGION_TEMPLATES: '/flameback/api/v1/region-templates',
  // Backend preview renderer: POST a TemplateConfig, get back the full page HTML
  // (byte-identical to page_builder.py's publish output). Not site-scoped.
  TEMPLATE_PREVIEW: '/flameback/api/v1/template-preview',
  // Public CSS/JS customization contract for published templates (not site-scoped)
  PUBLISHING_CONTRACT: '/flameback/api/v1/publishing/contract',
  // Indexing job endpoints
  INDEX: (siteId: string) => `/flameback/api/v1/sites/${siteId}/index`,
  BULK_INDEX: (siteId: string) => `/flameback/api/v1/sites/${siteId}/bulk-index`,
  BULK_INDEX_PRESIGN: (siteId: string) => `/flameback/api/v1/sites/${siteId}/bulk-index/presign`,
  BULK_INDEX_CONFIRM: (siteId: string) => `/flameback/api/v1/sites/${siteId}/bulk-index/confirm`,
  BULK_INDEX_HISTORY: (siteId: string) => `/flameback/api/v1/sites/${siteId}/bulk-index/history`,
  BULK_INDEX_MULTIPART_INITIATE: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/bulk-index/multipart/initiate`,
  BULK_INDEX_MULTIPART_COMPLETE: (siteId: string, jobId: string) =>
    `/flameback/api/v1/sites/${siteId}/bulk-index/multipart/${jobId}/complete`,
  BULK_INDEX_MULTIPART_ABORT: (siteId: string, jobId: string) =>
    `/flameback/api/v1/sites/${siteId}/bulk-index/multipart/${jobId}/abort`,
  INDEX_STATUS: (siteId: string) => `/flameback/api/v1/sites/${siteId}/index`,
  INDEX_CANCEL: (siteId: string) => `/flameback/api/v1/sites/${siteId}/index`,
  // Site content endpoints
  SITE_DOCUMENTS: (siteId: string) => `/flameback/api/v1/sites/${siteId}/documents`,
  DOCUMENT_CONTENT: (siteId: string, assetGuid: string) =>
    `/flameback/api/v1/sites/${siteId}/document/content?id=${encodeURIComponent(assetGuid)}`,
  DOCUMENT_METADATA: (siteId: string, assetGuid: string) =>
    `/flameback/api/v1/sites/${siteId}/document/metadata?id=${encodeURIComponent(assetGuid)}`,
  DELETE_DOCUMENTS: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/documents`,
  SITE_FOLDERS: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/folders`,
  SITE_NODES: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/nodes`,
  SITE_NODE_RESOLVE: (siteId: string, id: string) =>
    `/flameback/api/v1/sites/${siteId}/nodes/resolve?id=${encodeURIComponent(id)}`,
  DELETE_JOB: (siteId: string, jobId: string) =>
    `/flameback/api/v1/sites/${siteId}/delete-jobs/${encodeURIComponent(jobId)}`,
  ACTIVE_DELETE_JOB: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/delete-jobs`,
  PUBLISHED_URLS: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/documents/published-urls`,
  RESOLVE_ASSET_URLS: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/assets/resolve-urls`,
  SEARCH: (siteId: string) => `/data/api/v2/sites/${siteId}/search`,
  // Entity-dedup job lifecycle (GKG owns the job: trigger analysis/merge, poll, cancel)
  ENTITY_DEDUP_ANALYSIS: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/entity-dedup/analysis`,
  ENTITY_DEDUP_MERGE: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/entity-dedup/merge`,
  ENTITY_DEDUP_JOBS: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/entity-dedup/jobs`,
  // Entity-dedup candidate data (flameback-data DIRECT, searchDocuments precedent)
  ENTITY_DEDUP_CANDIDATES: (siteId: string) =>
    `/data/api/v1/sites/${siteId}/entity-dedup/candidates`,
  // Latest analysis scan for the site (flameback-data DIRECT) — lets the Review
  // tab resume a completed scan's candidates across sessions.
  ENTITY_DEDUP_LATEST_SCAN: (siteId: string) =>
    `/data/api/v1/sites/${siteId}/entity-dedup/latest-scan`,
  ENTITY_DEDUP_DECISIONS: (siteId: string) =>
    `/data/api/v1/sites/${siteId}/entity-dedup/mark-distinct`,
  ENTITY_DEDUP_AUDIT: (siteId: string) =>
    `/data/api/v1/sites/${siteId}/entity-dedup/audit`,
  // MCP config endpoint (client connection config)
  MCP_CONFIG: (siteId: string) => `/flameback/api/v1/sites/${siteId}/mcp-config`,
  // MCP tool configuration endpoint (tool name & description)
  MCP_TOOL_CONFIG: (siteId: string) => `/flameback/api/v1/sites/${siteId}/mcp-tool-config`,
  // RBAC endpoints
  ORG_MY_ACL: (orgId: string) => `/flameback/api/v1/orgs/${orgId}/users/me/acl`,
  // Org-level role CRUD
  ORG_ROLES: (orgId: string) =>
    `/flameback/api/v1/orgs/${orgId}/roles`,
  ORG_ROLE: (orgId: string, roleId: string) =>
    `/flameback/api/v1/orgs/${orgId}/roles/${encodeURIComponent(roleId)}`,
  // Org security — customer-controlled new-user access policy (MANAGE_ORG-gated)
  ORG_NEW_USER_POLICY: (orgId: string) =>
    `/flameback/api/v1/orgs/${orgId}/security/new-user-policy`,
  // Static permission catalog (display names + scope + assignability)
  PERMISSIONS: () => `/flameback/api/v1/permissions`,
  // Org-level user management
  ORG_USER_ROLE_LIST: (orgId: string) =>
    `/flameback/api/v1/orgs/${orgId}/user-roles`,
  ORG_USER_ACL: (orgId: string, userId: string) =>
    `/flameback/api/v1/orgs/${orgId}/users/${encodeURIComponent(userId)}/acl`,
  ORG_USER_ROLES: (orgId: string, userId: string) =>
    `/flameback/api/v1/orgs/${orgId}/users/${encodeURIComponent(userId)}/roles`,
  // Analytics endpoints have been migrated to flameback-platform.
  // See PLATFORM_ENDPOINTS for the new locations.
  // Widget configuration endpoint
  WIDGET_CONFIG: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/widget-config`,
  // Widget preview endpoint
  WIDGET_PREVIEW: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/widget-preview`,
  // API key management endpoints
  API_KEYS: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/api-keys`,
  API_KEY: (siteId: string, keyId: string) =>
    `/flameback/api/v1/sites/${siteId}/api-keys/${keyId}`,
  API_KEY_ASSIGN: (siteId: string, keyId: string) =>
    `/flameback/api/v1/sites/${siteId}/api-keys/${keyId}/assign`,
  // Prompt configuration endpoint
  PROMPT_CONFIG: (siteId: string, promptType: string) =>
    `/flameback/api/v1/sites/${siteId}/prompts/${promptType}`,
  // Cache controls endpoint
  CACHE_SETTINGS: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/cache/settings`,
  // Org resolution
  MY_ORGS: '/flameback/api/v1/users/me/orgs',
  // User management endpoints
  ENSURE_USER: (orgId: string) =>
    `/flameback/api/v1/orgs/${orgId}/users/me/ensure`,
  ACCEPT_TERMS: (orgId: string) =>
    `/flameback/api/v1/orgs/${orgId}/users/me/accept-terms`,
  GET_USER: (orgId: string) =>
    `/flameback/api/v1/orgs/${orgId}/users/me`,
  // Evaluation dataset endpoints
  EVAL_DATASETS: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/evals/datasets`,
  EVAL_DATASET: (siteId: string, datasetId: string) =>
    `/flameback/api/v1/sites/${siteId}/evals/datasets/${datasetId}`,
  // Evaluation experiment endpoints
  EVAL_EXPERIMENTS: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/evals/experiments`,
  EVAL_EXPERIMENT_STATUS: (siteId: string, experimentId: string) =>
    `/flameback/api/v1/sites/${siteId}/evals/experiments/${experimentId}/status`,
  EVAL_EXPERIMENT_RESULTS: (siteId: string, experimentId: string) =>
    `/flameback/api/v1/sites/${siteId}/evals/experiments/${experimentId}/results`,
  EVAL_CONTENT_OPTIMIZATION_SUGGEST: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/evals/content-optimization/suggest`,
  EVAL_CONTENT_OPTIMIZATION_APPLY: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/evals/content-optimization/apply`,
  // Conversion endpoints
  CONVERSION_UPLOAD: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/upload`,
  CONVERSION_HTML_URLS: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/html-urls`,
  CONVERSION_STATUS: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert`,
  CONVERSION_HEADINGS: (siteId: string, jobId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/${jobId}/headings`,
  CONVERSION_DITA_TYPES: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/dita-types`,
  CONVERSION_TOPICS: (siteId: string, jobId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/${jobId}/topics`,
  CONVERSION_TOPIC_CONTENT: (siteId: string, jobId: string, topicId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/${jobId}/topics/${topicId}/content`,
  CONVERSION_QUESTIONS: (siteId: string, jobId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/${jobId}/questions`,
  CONVERSION_MEDIA_URLS: (siteId: string, jobId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/${jobId}/media/urls`,
  CONVERSION_CHUNK: (siteId: string, jobId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/${jobId}/chunk`,
  // Human-gate answer endpoints (REST replaces the old WebSocket control messages).
  CONVERSION_JOBS: (siteId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/jobs`,
  CONVERSION_ANSWER: (siteId: string, jobId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/${jobId}/answer`,
  CONVERSION_RESOLVE_QUESTIONS: (siteId: string, jobId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/${jobId}/resolve-questions`,
  CONVERSION_REVIEW: (siteId: string, jobId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/${jobId}/review`,
  CONVERSION_CONFIRM: (siteId: string, jobId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/${jobId}/confirm`,
  CONVERSION_CANCEL: (siteId: string, jobId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/${jobId}/cancel`,
  CONVERSION_DOWNLOAD: (siteId: string, jobId: string) =>
    `/flameback/api/v1/sites/${siteId}/convert/${jobId}/download`,
  CONVERSION_WS: (siteId: string, jobId: string) =>
    `/flameback/ws/v1/sites/${siteId}/conversion/${jobId}/ws`,
};

// Command Center API Endpoints
export const CC_ENDPOINTS = {
  ORGANIZATIONS: '/platform/api/v1/command-center/organizations',
  ORGANIZATIONS_SUMMARY: '/platform/api/v1/command-center/organizations-summary',
  ORGANIZATION_SUMMARY: (orgId: string) =>
    `/platform/api/v1/command-center/organization-summary/${orgId}`,
  ORG_SITES: (orgId: string) =>
    `/platform/api/v1/command-center/organizations/${orgId}/sites`,
  ORG_SITE_DETAIL: (orgId: string, siteId: string) =>
    `/platform/api/v1/command-center/organizations/${orgId}/sites/${siteId}`,
  ORG_SITE_CONFIGURATION: (orgId: string, siteId: string) =>
    `/platform/api/v1/command-center/organizations/${orgId}/sites/${siteId}/configuration`,
  UPDATE_ORGANIZATION: (orgId: string) =>
    `/platform/api/v1/command-center/organizations/${orgId}`,
  ORG_ADMINS: (orgId: string) =>
    `/platform/api/v1/command-center/organizations/${orgId}/admins`,
  ORG_ADMIN_ENTRY: (orgId: string, userId: string) =>
    `/platform/api/v1/command-center/organizations/${orgId}/admins/${encodeURIComponent(userId)}`,
  ORG_ANALYTICS_DOMAINS: (orgId: string) =>
    `/platform/api/v1/command-center/organizations/${orgId}/analytics/domains`,
  DEFAULT_CONFIG: '/platform/api/v1/command-center/default-org-config',

  // Models — global system defaults (default model configuration per utility)
  SYSTEM_DEFAULTS: '/platform/api/v1/command-center/system-defaults',

  // Models — provider/model registry
  PROVIDER_MODEL_REGISTRY: '/platform/api/v1/command-center/provider-model-registry',
  PROVIDER_MODEL_REGISTRY_ENTRY: (registryId: string) =>
    `/platform/api/v1/command-center/provider-model-registry/${registryId}`,

  // Models — OOTB configurations
  OOTB_MODEL_CONFIGURATIONS: '/platform/api/v1/command-center/configurations',
  OOTB_MODEL_CONFIGURATION: (configurationId: string) =>
    `/platform/api/v1/command-center/configurations/${configurationId}`,
  OOTB_MODEL_CONFIGURATION_CONFIG: (configurationId: string) =>
    `/platform/api/v1/command-center/configurations/${configurationId}/config`,

  // Org model allowlist (per pool)
  ORG_ALLOWLIST_OOTB: (orgId: string) =>
    `/platform/api/v1/command-center/${orgId}/allowlist/ootb`,
  ORG_ALLOWLIST_CUSTOM: (orgId: string) =>
    `/platform/api/v1/command-center/${orgId}/allowlist/custom`,
};

/**
 * Org-facing "Models" landing page (design-frames/12-landing-models.html) —
 * org-member-gated (not CC-admin), so a separate base path from CC_ENDPOINTS
 * above: /platform/api/v1/models/{orgId}/... rather than /command-center/...
 */
export const ORG_MODELS_ENDPOINTS = {
  CONFIGURATIONS: (orgId: string) => `/platform/api/v1/models/${orgId}/configuration`,
  CONFIGURATION: (orgId: string, configurationId: string) =>
    `/platform/api/v1/models/${orgId}/configuration/${configurationId}`,
  CONFIGURATION_CONFIG: (orgId: string, configurationId: string) =>
    `/platform/api/v1/models/${orgId}/configuration/${configurationId}/config`,
  CONFIGURATION_RESET: (orgId: string, configurationId: string) =>
    `/platform/api/v1/models/${orgId}/configuration/${configurationId}/reset`,
  CONFIGURATIONS_ALLOWLIST: (orgId: string) => `/platform/api/v1/models/${orgId}/configurations/allowlist`,
  CONFIGURATIONS_USAGE: (orgId: string) => `/platform/api/v1/models/${orgId}/configurations/usage`,
  UTILITY_ASSIGNMENTS: (orgId: string) => `/platform/api/v1/models/${orgId}/utility-assignments`,
  REGISTRY_PROVIDERS: (orgId: string) => `/platform/api/v1/models/${orgId}/registry/providers`,
  REGISTRY_MODELS: (orgId: string) => `/platform/api/v1/models/${orgId}/registry/models`,
};

/**
 * Domain <-> model-configuration attachment: org-member-gated, used by the
 * domain create/edit picker. Split into two: a paginated per-utility options
 * list (API 1) and an org-level "can create a domain right now" check (API 2).
 */
export const DOMAIN_MODEL_ENDPOINTS = {
  UTILITY_CONFIGURATIONS: (orgId: string, utilityName: string) =>
    `/platform/api/v1/orgs/${orgId}/domain-utilities/${utilityName}/configurations`,
  REQUIRED_STATUS: (orgId: string) => `/platform/api/v1/orgs/${orgId}/domain-utilities/required-status`,
};

// IMS Endpoints
// AUTHORIZE uses the full IMS URL because it's a browser redirect (no CORS issue).
// TOKEN, PROFILE, and VALIDATE are fetch/XHR calls which would be blocked by CORS,
// so they go through the nginx reverse proxy at the same origin (/ims/...).
export const IMS_ENDPOINTS = {
  AUTHORIZE: `${OAUTH_CONFIG.IMS_ENDPOINT}/ims/authorize/v2`,
  LOGOUT: `${OAUTH_CONFIG.IMS_ENDPOINT}/ims/logout/v1`,
  TOKEN: IS_LOCAL ? '' : '/ims/token/v3',
  PROFILE: IS_LOCAL ? '' : '/ims/profile/v1',
  VALIDATE: IS_LOCAL ? '' : '/ims/validate_token/v1',
};

// Get auth token from storage
export const getAuthToken = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
};

// Request headers with auth
export const getHeaders = (): HeadersInit => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    'x-ims-clientid': OAUTH_CONFIG.CLIENT_ID,
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
  };
};
